import type * as Sentry from '@sentry/remix';

export function getMessageFilters(): (string | RegExp)[] {
  return [
    'Failed to fetch',
    'NetworkError',
    'Load failed',
    'InvalidStateError',
    'UnhandledRejection',
    /Unexpected token.+not valid JSON/i,
    "Cannot read properties of null (reading 'document')",
    'a.contentWindow',
    '__CF$cv',
    '__CFScv',
    'window.webkit.messageHandlers',
    'There was an error during concurrent rendering',
    'Java object is gone',
    'AbortError',
    'SecurityError',
    'FirebaseError',
    'RefreshPageError',
  ];
}

export function getStacktraceFilters(): (string | RegExp)[] {
  return [
    'googletagmanager.com/gtm.js',
    'cdn.birdie.so',
    'redditstatic.com/ads/pixel.js',
    /^blob:/,
    'digitaloceanspaces.com/pixel.min.js',
    'embla-carousel',
    'convertexperiments.com',
    'platform.twitter.com/widgets.js',
    'hyros.com',
  ];
}

export function getBreadcrumbFilters(): (string | RegExp)[] {
  return ['birdie.so'];
}

export function sentryClientFilter(event: Sentry.ErrorEvent, _hint: Sentry.EventHint) {
  if (!event || !event.exception) return true;

  // Filter errors not caught by our ErrorBoundary (outside our app's control)
  // If the error was caught and rendered by our Fallback UI, it will have this tag
  // Errors without this tag occurred during initialization or in third-party code
  const isFallbackRendered = event.tags?.render === 'fallback_ui_error';
  if (!isFallbackRendered) {
    return true; // Filter out - not actionable for our team
  }

  // Filter exception type/message (these are transient errors and just adds noise to Sentry)
  const filterPatterns = getMessageFilters();
  const filtered = event.exception.values?.some((e) =>
    filterPatterns.some((p) =>
      p instanceof RegExp ? p.test(e.type ?? '') || p.test(e.value ?? '') : e.type?.includes(p) || e.value?.includes(p),
    ),
  );

  // Exclude certain patterns from being sent to sentry
  // Array of regex patterns to match against the filename/abs_path of the frame
  const excludePatterns = getStacktraceFilters();
  const excluded = event.exception.values?.some((e) =>
    e.stacktrace?.frames?.some((f) =>
      excludePatterns.some((p: string | RegExp) =>
        p instanceof RegExp
          ? p.test(f.filename ?? '') || p.test(f.abs_path ?? '')
          : f.filename?.includes(p) || f.abs_path?.includes(p),
      ),
    ),
  );

  // Filter events where breadcrumb matches certain patterns
  // Unfortunately, some exceptions are meaningless without context of the breadcrumbs
  // checking if any breadcrumb matches the filter patterns
  // Array of regex patterns to match against the breadcrumb message or data.url
  const breadcrumbFilters = getBreadcrumbFilters();
  const breadcrumbs = Array.from(event.breadcrumbs || [])
    .reverse()
    .slice(0, 3); // check last 3 breadcrumbs
  const breadcrumbFiltered = breadcrumbs.some((b) =>
    breadcrumbFilters.some((p) =>
      p instanceof RegExp
        ? p.test(b.message ?? '') || p.test(b?.data?.url ?? '')
        : (typeof b.message === 'string' && b.message.includes(p)) ||
          (typeof b?.data?.url === 'string' && b.data.url.includes(p)),
    ),
  );

  if (filtered || excluded || breadcrumbFiltered) {
    return true;
  }
  return false;
}
